import { isDescriptionHtml } from '@nudool/shared';
import Bold from '@tiptap/extension-bold';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import { BulletList, ListItem } from '@tiptap/extension-list';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { UndoRedo } from '@tiptap/extensions';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { BoldIcon, ListIcon } from 'lucide-react';
import * as React from 'react';

import { DimeToggle } from './DimeToggle.js';
import { plainTextToHtml } from './lib/plain-text-to-html.js';
import { cn } from './lib/utils.js';

const BoldAsB = Bold.extend({
  renderHTML: ({ HTMLAttributes }) => ['b', HTMLAttributes, 0],
});

const EXTENSIONS = [Document, Paragraph, Text, BoldAsB, HardBreak, BulletList, ListItem, UndoRedo];

const EDITOR_CLASS =
  'min-h-16 w-full rounded-b-lg border border-input bg-transparent px-2.5 py-2 text-base outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 md:text-sm dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_b]:font-semibold [&_li>p]:mt-0 [&_p+p]:mt-2 [&_p+ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul+p]:mt-2';

const keepEditorFocus = (event: React.MouseEvent) => event.preventDefault();

const toEditorContent = (value: string) =>
  isDescriptionHtml(value) ? value : plainTextToHtml(value);

export type DimeRichTextEditorProps = Readonly<{
  id?: string;
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}>;

export function DimeRichTextEditor({
  id,
  value,
  onChange,
  onBlur,
  disabled = false,
  className,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: DimeRichTextEditorProps) {
  const emittedValueRef = React.useRef(value);
  const onChangeRef = React.useRef(onChange);
  const onBlurRef = React.useRef(onBlur);
  React.useEffect(() => {
    onChangeRef.current = onChange;
    onBlurRef.current = onBlur;
  });

  const attributes = React.useMemo(
    () => ({
      ...(id && { id }),
      role: 'textbox',
      'aria-multiline': 'true',
      ...(ariaLabelledBy && { 'aria-labelledby': ariaLabelledBy }),
      ...(ariaDescribedBy && { 'aria-describedby': ariaDescribedBy }),
      ...(ariaInvalid && { 'aria-invalid': 'true' }),
      ...(disabled && { 'aria-disabled': 'true' }),
      class: cn(EDITOR_CLASS, disabled && 'cursor-not-allowed bg-input/50 opacity-50'),
    }),
    [id, ariaLabelledBy, ariaDescribedBy, ariaInvalid, disabled],
  );

  const editor = useEditor({
    extensions: EXTENSIONS,
    editorProps: { attributes },
    content: toEditorContent(value),
    editable: !disabled,
    onUpdate: ({ editor: updatedEditor }) => {
      const html = updatedEditor.isEmpty ? '' : updatedEditor.getHTML();
      emittedValueRef.current = html;
      onChangeRef.current(html);
    },
    onBlur: () => onBlurRef.current?.(),
  });

  React.useEffect(() => {
    if (value === emittedValueRef.current) return;
    emittedValueRef.current = value;
    editor.commands.setContent(toEditorContent(value), { emitUpdate: false });
  }, [editor, value]);

  React.useEffect(() => {
    editor.setEditable(!disabled, false);
  }, [editor, disabled]);

  React.useEffect(() => {
    editor.setOptions({ editorProps: { attributes } });
  }, [editor, attributes]);

  const active = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current.isActive('bold'),
      bulletList: current.isActive('bulletList'),
    }),
  });

  return (
    <div data-slot="rich-text-editor" className={cn('w-full', className)}>
      <div
        role="toolbar"
        aria-label="Formatting"
        className="flex gap-1 rounded-t-lg border border-b-0 border-input p-1"
      >
        <DimeToggle
          size="sm"
          onMouseDown={keepEditorFocus}
          aria-label="Bold"
          disabled={disabled}
          pressed={active.bold}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
        >
          <BoldIcon />
        </DimeToggle>
        <DimeToggle
          size="sm"
          onMouseDown={keepEditorFocus}
          aria-label="Bulleted list"
          disabled={disabled}
          pressed={active.bulletList}
          onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        >
          <ListIcon />
        </DimeToggle>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
